// src/edu-world/systems/path-trail.ts
// Path Trail: dotted line of small transparent circles from player to mission target.
// Updates position each frame. Friendly, subtle, non-blocking visual guide.

import Phaser from 'phaser';

export class PathTrail {
  private scene: Phaser.Scene;
  private dots: Phaser.GameObjects.Arc[] = [];
  private targetPosition: { x: number; y: number } | null = null;
  private isVisible: boolean = false;

  private readonly DOT_COUNT = 8;
  private readonly DOT_RADIUS = 4;
  private readonly DOT_COLOR = 0xffd54f; // soft gold
  private readonly DOT_ALPHA = 0.35;
  private readonly MIN_DISTANCE = 30; // don't show if player is very close

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Pre-create dots (hidden)
    for (let i = 0; i < this.DOT_COUNT; i++) {
      const dot = scene.add.circle(0, 0, this.DOT_RADIUS, this.DOT_COLOR, this.DOT_ALPHA);
      dot.setDepth(3);
      dot.setVisible(false);
      this.dots.push(dot);
    }
  }

  /** Show the trail pointing to target */
  show(targetPos: { x: number; y: number }): void {
    this.targetPosition = targetPos;
    this.isVisible = true;
  }

  /** Hide and clear the trail */
  hide(): void {
    this.isVisible = false;
    this.targetPosition = null;
    for (const dot of this.dots) {
      dot.setVisible(false);
    }
  }

  /** Update trail positions based on current player position. Call every frame. */
  update(playerPos: { x: number; y: number }): void {
    if (!this.isVisible || !this.targetPosition) {
      return;
    }

    const dx = this.targetPosition.x - playerPos.x;
    const dy = this.targetPosition.y - playerPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Hide if player is too close to target
    if (distance < this.MIN_DISTANCE) {
      for (const dot of this.dots) {
        dot.setVisible(false);
      }
      return;
    }

    // Position dots evenly between player and target (skip first 20% near player)
    const startFraction = 0.15;
    const endFraction = 0.85;

    for (let i = 0; i < this.DOT_COUNT; i++) {
      const t = startFraction + (i / (this.DOT_COUNT - 1)) * (endFraction - startFraction);
      const dotX = playerPos.x + dx * t;
      const dotY = playerPos.y + dy * t;

      const dot = this.dots[i];
      dot.setPosition(dotX, dotY);
      dot.setVisible(true);

      // Dots closer to target are slightly larger and more opaque
      const scale = 0.7 + t * 0.6;
      dot.setScale(scale);
      dot.setAlpha(this.DOT_ALPHA * (0.5 + t * 0.5));
    }
  }

  /** Clean up */
  destroy(): void {
    for (const dot of this.dots) {
      dot.destroy();
    }
    this.dots = [];
  }
}
